import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { subMonths } from 'date-fns';
import { takeUntil } from 'rxjs/operators';

import { ComponentBase } from '@sb-shared/components/component-base/component-base';
import { SimpleAlertService } from '@sb-shared/services/simple-alert.service';
import { BalanceService } from '../../balance.service';

interface AccountData {
  accountHolderId: number;
  accountHolderName: string;
  transactions: any[];
}

interface TransactionSearch {
  text: string;
  from: Date;
  to: Date;
}

@Component({
  selector: 'sb-current-user-transaction-view',
  templateUrl: './current-user-transaction-view.component.html',
  styleUrls: ['./current-user-transaction-view.component.scss'],
  standalone: false
})
export class CurrentUserTransactionViewComponent extends ComponentBase {

  private _accountTypeId: number;

  @Input()
  set accountTypeId(value: number) {
    this._accountTypeId = value;
    if (value) {
      this.tryAndGetTransactions();
    }
  }
  get accountTypeId(): number {
    return this._accountTypeId;
  }

  loading = 0;
  accountData: AccountData = null;
  numberOfTransactions = 0;
  showSourceTransactions = false;
  showRelatedTransactions = false;
  fetchedSource = false;
  sortType = 'date';
  sortReverse = true;
  search: TransactionSearch = {
    text: '',
    from: subMonths(new Date(), 3),
    to: new Date()
  };
  currencyDisplaySymbol: string;

  constructor(
    public activeModal: NgbActiveModal,
    private balanceService: BalanceService,
    private simpleAlertService: SimpleAlertService
  ) {
    super();
    this.currencyDisplaySymbol = (window as any)?.EveryBuddy?.Constants?.CurrencyDisplaySymbol ?? '';
  }

  close(): void {
    this.activeModal.dismiss();
  }

  formatCurrency(value: number): string {
    if (value == null || isNaN(value)) {
      return '';
    }
    const absFixed = Math.abs(value).toFixed(2);
    return value < 0
      ? `-${this.currencyDisplaySymbol}${absFixed}`
      : `${this.currencyDisplaySymbol}${absFixed}`;
  }

  fetchSource(): void {
    setTimeout(() => {
      if (!this.showSourceTransactions || this.fetchedSource) {
        return;
      }
      this.tryAndGetTransactions();
    }, 0);
  }

  tryAndGetTransactions(): void {
    if (!this.accountTypeId) {
      return;
    }

    this.loading++;
    this.numberOfTransactions = 0;
    this.accountData = {
      accountHolderId: 0,
      accountHolderName: '',
      transactions: []
    };

    this.balanceService
      .getTransactionsForAccount(this.accountTypeId, this.showSourceTransactions, this.search.from, this.search.to)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: data => {
          this.accountData.accountHolderId = data.accountHolderId;
          this.accountData.accountHolderName = data.accountHolderName;

          const transactions = this.arrangeTransactionData(
            data.transactions,
            data.openingBalance,
            this.showSourceTransactions,
            this.accountData.accountHolderId
          );

          this.fetchedSource = this.showSourceTransactions;

          this.copyTransactionsWhichShouldBeVisible(
            transactions,
            this.accountData.transactions,
            this.search.from,
            this.search.to
          );

          this.numberOfTransactions = this.accountData.transactions.length;
          this.loading--;
        },
        error: err => {
          this.simpleAlertService.errorAlert();
          console.error(err);
          this.loading--;
        }
      });
  }

  customFilter = (value: any): boolean => {
    if (!this.accountData) {
      return false;
    }
    const text = (this.search.text || '').toLowerCase();
    return value.accountOwnerId === this.accountData.accountHolderId &&
      (value.description || '').toLowerCase().indexOf(text) > -1;
  };

  filteredTransactions(): any[] {
    if (!this.accountData?.transactions) {
      return [];
    }
    const list = this.accountData.transactions.filter(this.customFilter);
    const dir = this.sortReverse ? -1 : 1;
    return list.slice().sort((a, b) => {
      const aVal = a[this.sortType];
      const bVal = b[this.sortType];
      const aCmp = aVal instanceof Date ? aVal.getTime() : aVal;
      const bCmp = bVal instanceof Date ? bVal.getTime() : bVal;
      if (aCmp < bCmp) {
        return -1 * dir;
      }
      if (aCmp > bCmp) {
        return 1 * dir;
      }
      return 0;
    });
  }

  // Ported from AngularJS balanceSharedService.arrangeTransactionData
  private arrangeTransactionData(
    transactions: any[],
    openingBalance: number,
    includeSource: boolean,
    accountOwnerId: number
  ): any[] {
    transactions.push({
      id: 0,
      accountOwnerId,
      balance: openingBalance,
      credit: 0.0,
      debit: 0.0,
      reference: '',
      description: '',
      date: '1900-01-01T00:00:00',
      isOpeningBalance: true
    });

    let outerBreakJ = 0;
    for (let i = transactions.length - 1; i >= 0; i--) {
      const parentTransaction = transactions[i];
      parentTransaction.relatedTransactions = [];
      parentTransaction.sourceTransactions = [];
      parentTransaction.sourceTransactionIds = [];
      parentTransaction.date = new Date(parentTransaction.date);

      for (let j = transactions.length - 1; j >= 0; j--) {
        if ((transactions[j].relatedTransactionId === parentTransaction.id &&
          transactions[j].accountOwnerId === accountOwnerId) ||
          (parentTransaction.relatedTransactionId === transactions[j].id &&
            transactions[j].accountOwnerId === accountOwnerId)) {
          parentTransaction.relatedTransactions.push(transactions[j]);
        }

        if (includeSource &&
          parentTransaction.accountOwnerId === accountOwnerId &&
          transactions[j].relatedTransactionId &&
          ((transactions[j].sourceTransactionId === parentTransaction.id) ||
            (parentTransaction.sourceTransactionId === transactions[j].id))) {
          parentTransaction.sourceTransactionIds.push(transactions[j].relatedTransactionId);
        }
      }

      if (includeSource) {
        for (let j = transactions.length - 1; j >= 0; j--) {
          if (parentTransaction.accountOwnerId === accountOwnerId &&
            ((parentTransaction.sourceTransactionIds.indexOf(transactions[j].id) > -1) ||
              (parentTransaction.sourceTransactionIds.indexOf(transactions[j].relatedTransactionId) > -1))) {
            parentTransaction.sourceTransactions.push(transactions[j]);
          }
        }
      }

      if (transactions[i].balancePendingUpdate) {
        console.log('balance.components.currentUsertransactionView/found transaction with balancePendingUpdate', transactions[i]);

        let balance = openingBalance;
        outerBreakJ = i - 1;

        if (i > 0) {
          for (let j = i - 1; j >= 0; j--) {
            if (!transactions[j].balancePendingUpdate) {
              balance = transactions[j].balance;
              outerBreakJ = j;
              break;
            }
          }
        }

        for (let k = outerBreakJ + 1; k <= i; k++) {
          balance += transactions[k].credit;
          balance -= transactions[k].debit;
          transactions[k].balance = balance;
        }

        break;
      }
    }

    return transactions;
  }

  // Ported from AngularJS balanceSharedService.copyTransactionsWhichShouldBeVisible
  private copyTransactionsWhichShouldBeVisible(source: any[], dest: any[], fromDate: Date, toDate: Date): void {
    for (let i = 0; i < source.length; i++) {
      const currentTransactionDate = source[i].date;

      if ((currentTransactionDate > fromDate || source[i].isOpeningBalance) &&
        currentTransactionDate < toDate) {
        dest.push(source[i]);
      }
    }
  }
}
